/**
 * useCortexBackgroundJobs — hook for querying and subscribing to Cortex background job status.
 *
 * Fetches cortex_task_log for the current user and subscribes to the
 * notifications table via Supabase Realtime for live status updates.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRealtimeTable } from "@/hooks/useRealtimeTable";
import type { RealtimePayload } from "@/types/realtime";
import type { Notification } from "@/types/notification";
import { logger } from "@/lib/logger";

/** Shape of a cortex_task_log row for UI display */
export interface CortexBackgroundJob {
  id: string;
  org_id: string | null;
  intent: string;
  status:
    | "queued"
    | "processing"
    | "awaiting_review"
    | "awaiting_event"
    | "completed"
    | "failed";
  context: {
    prompt?: string;
    specialist?: string | null;
    backgroundJob?: boolean;
    submittedAt?: string;
    workflowRun?: {
      workflowId: string;
      workflowVersion: number;
      workflowName: string;
    };
    [key: string]: unknown;
  };
  error: string | null;
  created_at: string;
  processed_at: string | null;
  specialist_used: string | null;
  execution_duration_ms: number | null;
  tokens_consumed: number | null;
  human_review_required: boolean | null;
}

/** Shape of a workflow_gates row — a durable review/event gate (Phase 8). */
export interface WorkflowGate {
  id: string;
  run_id: string;
  task_log_id: string | null;
  kind: "human_review" | "wait_for_event";
  status: "open" | "resolved" | "rejected" | "expired";
  step_index: number;
  label: string;
  reason: string | null;
  domain: string | null;
  event_type: string | null;
  user_id: string | null;
  org_id: string | null;
  created_at: string;
}

interface UseCortexBackgroundJobsOptions {
  limit?: number;
  /** Only show background jobs (context->backgroundJob = true) */
  backgroundOnly?: boolean;
}

export function useCortexBackgroundJobs(options: UseCortexBackgroundJobsOptions = {}) {
  const { limit = 10, backgroundOnly = true } = options;
  const { user } = useAuth();
  const [jobs, setJobs] = useState<CortexBackgroundJob[]>([]);
  const [pendingGates, setPendingGates] = useState<Record<string, WorkflowGate>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch jobs from cortex_task_log, plus any open workflow gates attached
  // to those jobs (the human_review approval card data source).
  const fetchJobs = useCallback(async () => {
    if (!user?.id) {
      setJobs([]);
      setPendingGates({});
      setIsLoading(false);
      return;
    }

    try {
      let query = (supabase as any)
        .from("cortex_task_log")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (backgroundOnly) {
        // Include both regular background jobs and workflow runs
        query = query.or("intent.eq.background_job,intent.like.workflow_run%");
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        setError(fetchError.message);
        return;
      }

      const rows = (data as CortexBackgroundJob[]) ?? [];
      setJobs(rows);

      const jobIds = rows.map((j) => j.id);
      if (jobIds.length > 0) {
        const { data: gateRows } = await (supabase as any)
          .from("workflow_gates")
          .select("*")
          .in("task_log_id", jobIds)
          .eq("status", "open");
        const byTaskLog: Record<string, WorkflowGate> = {};
        for (const g of (gateRows as WorkflowGate[]) ?? []) {
          if (g.task_log_id) byTaskLog[g.task_log_id] = g;
        }
        setPendingGates(byTaskLog);
      } else {
        setPendingGates({});
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch jobs");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, limit, backgroundOnly]);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Subscribe to notifications for live Cortex job status updates via the
  // canonical `useRealtimeTable` hook (Phase 3 migration).
  useRealtimeTable("notifications", {
    enabled: Boolean(user?.id),
    filter: user?.id
      ? { column: "user_id", operator: "eq" as const, value: user.id }
      : undefined,
    events: ["INSERT"],
    onChange: (payload: RealtimePayload<Notification>) => {
      const notification = payload.new as {
        type?: string;
        entity_type?: string;
        entity_id?: string;
      } | undefined;
      if (
        notification?.type === "CORTEX_JOB_COMPLETED" ||
        notification?.type === "CORTEX_JOB_FAILED" ||
        notification?.type === "CORTEX_JOB_QUEUED" ||
        notification?.type === "WORKFLOW_REVIEW_REQUESTED"
      ) {
        logger.info("[useCortexBackgroundJobs] Job notification received, refreshing");
        fetchJobs();
      }
    },
  });

  return {
    jobs,
    pendingGates,
    isLoading,
    error,
    refetch: fetchJobs,
  };
}
